import { RouterProvider, createBrowserRouter } from "react-router-dom";

import ErrorBoundary from "~/components/layout/ErrorBoundary";
import { AuthenticatedRoot } from "~/components/layout/AuthenticatedRoot";
import BasicRoot from "~/components/layout/BasicRoot";
import LoginCallbackScreen from "~/components/login/LoginCallbackScreen";
import SignInScreen from "~/components/login/SignInScreen";
import SchedulerEmptyScreen from "~/components/scheduler/SchedulerEmptyScreen";
import IndexView from "~/views/_index";
import LocationIndexView from "~/views/location";
import CommandIndexView from "~/views/command";
import SosAlertsHistoryView from "~/views/sos-alerts";
import SchedulerLogView, { MostRecentSchedulerLogView, RecentSchedulerLogIndexView } from "./views/debug";
import { OptimizerLogIndexView } from "./views/debug/optimizer-log";
import PatientsPage from "./views/patients";
import PatientIntakeList from "./views/patients/PatientIntakeList";
import SchedulerVisitScreen from "./views/scheduler/visit/$visitId";
import SchedulerPatientScreen from "./views/scheduler/patient/$patientId";
import { SnackbarProvider } from "./hooks/useSnackbar";
import { AuthProvider } from "./state/auth";
import { GraphqlProvider } from "./providers/GraphqlProvider";
import MarlinThemeProvider from "./components/layout/MarlinThemeProvider";
import KPIDashboardView from "./views/kpis";
import OperateConfig from "./views/operate-config";
import PredictConfig from "./views/predict-config";
import AvailabilityManagement from "./views/availability";
import VisitAlerts from "./views/visit-alerts";
import VisitLocationIndexView from "./views/visit-location";
import DriveTimeOptimizerModule from "./views/debug/test-drive";
import { ZonedDateFormatProvider } from "./providers/ZonedDateFormatProvider";
import { TodaysVisitsProvider } from "./providers/TodaysVisitsProvider";
import DemoDataScreen from "./views/demo-data";
import FHIRCallbackScreen from "./components/login/FHIRCallbackScreen";
import DemoPatientDetailView from "./views/demo/DemoPatientDetailView";
import EHRDemo from "./views/ehr-demo/EHRDemo";
import { OrgConfigProvider } from "./providers/OrgConfigProvider";
import CandidacyList from "./views/candidacy/CandidacyList";
import { ReactNode } from "react";
import UsersPage from "./views/users";
import SecurityPage from "./views/security";
import IntegrationsConfig from "./views/integrations";
import SupplyItemList from "./views/supplies";
import { WebSocketProvider } from "./providers/WebSocketProvider";

const tabsPage = (path: string, element: ReactNode) => ({
  path,
  children: [
    {
      index: true,
      element,
    },
    {
      path: ":tab/:id",
      element,
    },
    {
      path: ":tab",
      element,
    },
  ],
})

import CapacityManagementRoute from "./features/capacity-management";
import { CapacityRoutes, HomeHealthFreshOnReseed, MyWeekRoutes, PendingAdmissionsRoutes, ReferralsRoutes } from "./features/home-health-demo";
import {
  PredictAllPatients,
  PredictMyPatients,
  PredictUnassignedPatients,
  PredictPatientDetail,
  PredictKPIs
} from "./views/predict";
import PredictLayout from "./components/predict/PredictLayout";
import {
  CoordinatePatientList,
  CoordinateMyPatients,
  CoordinatePatientDetail,
  CoordinateKPIs,
} from "./views/coordinate";
import CoordinateLayout from "./components/coordinate/CoordinateLayout";
import {
  DischargeAllPatients,
  DischargeMyPatients,
  DischargeUnassignedPatients,
  DischargePatientDetail,
} from "./views/discharge";
import DischargeLayout from "./components/discharge/DischargeLayout";
import {
  TransitionPipelineView,
  TransitionListView,
  TransitionReferralDetail,
} from "./views/transition";
import TransitionLayout from "./components/transition/TransitionLayout";
import { TransitionAttentionProvider } from "./providers/TransitionAttentionProvider";
import { NotificationProvider } from "./providers/NotificationProvider";
import { IntakeQueueProvider } from "./providers/IntakeQueueProvider";
import { AppProviderBridge } from "./providers/AppProviderBridge";
import { CoordinateProvider } from "./providers/CoordinateProvider";
import { DischargeProvider } from "./providers/DischargeProvider";

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <ErrorBoundary>
        <GraphqlProvider>
          <OrgConfigProvider>
            <WebSocketProvider>
              <SnackbarProvider>
                <TodaysVisitsProvider>
                  <TransitionAttentionProvider>
                  <NotificationProvider>
                    <IntakeQueueProvider>
                      <CoordinateProvider>
                        <DischargeProvider>
                          <AppProviderBridge>
                            <AuthenticatedRoot />
                            </AppProviderBridge>
                        </DischargeProvider>
                      </CoordinateProvider>
                    </IntakeQueueProvider>
                  </NotificationProvider>
                </TransitionAttentionProvider>
              </TodaysVisitsProvider>
              </SnackbarProvider>
            </WebSocketProvider>
          </OrgConfigProvider>
        </GraphqlProvider>
      </ErrorBoundary>
    ),
    children: [
      {
        index: true,
        element: <IndexView />,
      },
      {
        path: "/scheduler",
        children: [
          {
            index: true,
            element: <SchedulerEmptyScreen />,
          },
          {
            path: "visit/:visitId",
            element: <SchedulerVisitScreen />,
          },
          {
            path: "patient/:patientId",
            element: <SchedulerPatientScreen />,
          },
        ],
      },
      {
        path: "/command",
        element: <HomeHealthFreshOnReseed><CommandIndexView /></HomeHealthFreshOnReseed>,
      },
      {
        path: "/sos-alerts",
        element: <SosAlertsHistoryView />,
      },
      {
        path: "/visit-alerts",
        element: <VisitAlerts />,
      },
      {
        path: "/location",
        element: <LocationIndexView />,
      },
      {
        path: "/visit-location/:date",
        element: <VisitLocationIndexView />,
      },
      {
        path: "/scheduler-log",
        element: <RecentSchedulerLogIndexView />,
      },
      {
        path: "/scheduler-log/last",
        element: <MostRecentSchedulerLogView />,
      },
      {
        path: "/scheduler-log/:visitId",
        element: <SchedulerLogView />,
      },
      {
        path: "/optimizer-log",
        element: <OptimizerLogIndexView />,
      },
      {
        path: "/optimizer-log/:id",
        element: <OptimizerLogIndexView />,
      },
      {
        path: "/intake",
        element: <PatientIntakeList />,
      },
      {
        path: "/test-drive",
        element: <DriveTimeOptimizerModule />,
      },
      {
        path: "/supplies",
        element: <SupplyItemList />,
      },
      tabsPage("/patients", <PatientsPage />),
      {
        path: "/kpis",
        element: <KPIDashboardView />,
      },
      tabsPage("/operate-config", <OperateConfig />),
      tabsPage("/predict-config", <PredictConfig />),
      tabsPage("/users", <UsersPage />),
      {
        path: "/availability",
        element: <AvailabilityManagement />,
      },
      tabsPage("/security", <SecurityPage />),
      tabsPage("/integrations", <IntegrationsConfig />),
      {
        path: "/demo-data",
        element: <DemoDataScreen />,
      },
      // {
      //   path: "/predict",
      //   children: [
      //     {
      //       index: true,
      //       element: <CandidacyList ownershipFilter="ALL" />,
      //     },
      //     {
      //       path: "my-patients",
      //       element: <CandidacyList ownershipFilter="MY" />,
      //     },
      //     {
      //       path: "unassigned-patients",
      //       element: <CandidacyList ownershipFilter="UNASSIGNED" />,
      //     },
      //   ],
      // },
      {
        path: "/demo-patients/:patientId",
        element: <DemoPatientDetailView />,
      },
      {
        path: "/capacity-management",
        element: <CapacityManagementRoute />,
      },
      {
        path: "/capacity/*",
        element: <CapacityRoutes />,
      },
      {
        path: "/referrals/*",
        element: <ReferralsRoutes />,
      },
      {
        path: "/pending-admissions/*",
        element: <PendingAdmissionsRoutes />,
      },
      {
        path: "/my-week/*",
        element: <MyWeekRoutes />,
      },
      {
        path: "/predict",
        element: <PredictLayout />,
        children: [
          {
            index: true,
            element: <PredictAllPatients />,
          },
          {
            path: "all-patients",
            element: <PredictAllPatients />,
          },
          {
            path: "my-patients",
            element: <PredictMyPatients />,
          },
          {
            path: "unassigned-patients",
            element: <PredictUnassignedPatients />,
          },
          {
            path: "kpis",
            element: <PredictKPIs />,
          },
          {
            path: "patient/:patientId",
            element: <PredictPatientDetail />,
          },
        ],
      },
      {
        path: "/discharge",
        element: <DischargeLayout />,
        children: [
          {
            index: true,
            element: <DischargeAllPatients />,
          },
          {
            path: "all-patients",
            element: <DischargeAllPatients />,
          },
          {
            path: "my-patients",
            element: <DischargeMyPatients />,
          },
          {
            path: "unassigned-patients",
            element: <DischargeUnassignedPatients />,
          },
          {
            path: "patient/:patientId",
            element: <DischargePatientDetail />,
          },
        ],
      },
      {
        path: "/coordinate",
        element: <CoordinateLayout />,
        children: [
          {
            path: "all-patients",
            element: <CoordinatePatientList />,
          },
          {
            path: "patients",
            element: <CoordinatePatientList />,
          },
          {
            path: "my-patients",
            element: <CoordinateMyPatients />,
          },
          {
            path: "patient/:patientId",
            element: <CoordinatePatientDetail />,
          },
          {
            path: "kpis",
            element: <CoordinateKPIs />,
          },
        ],
      },
      {
        path: "/transition",
        element: <TransitionLayout />,
        children: [
          {
            index: true,
            element: <TransitionPipelineView />,
          },
          {
            path: "pipeline",
            element: <TransitionPipelineView />,
          },
          {
            path: "list",
            element: <TransitionListView />,
          },
          {
            path: "referral/:referralId",
            element: <TransitionReferralDetail />,
          },
        ],
      },
      {
        // Fallback; unknown paths direct to the command center, rather than throwing an error and the app
        // failing to load.
        path: "*",
        element: <IndexView />,
      },
    ],
  },
  {
    path: "/ehr-demo",
    element: <EHRDemo />,
  },
  {
    path: "/ehr-demo/predict",
    element: <EHRDemo variant="predict" />,
  },
  {
    path: "/ehr-demo/coordinate",
    element: <EHRDemo variant="coordinate" />,
  },
  {
    element: <BasicRoot />,
    children: [
      {
        path: "/login",
        element: <SignInScreen />,
      },
      {
        path: "/auth/cognito/callback",
        element: <LoginCallbackScreen />,
      },
      {
        path: "/auth/fhir/callback",
        element: <FHIRCallbackScreen />,
      },
    ],
  },
]);

function App() {
  return (
    <MarlinThemeProvider>
      <AuthProvider>
        <ZonedDateFormatProvider>
          <RouterProvider router={router} />
        </ZonedDateFormatProvider>
      </AuthProvider>
    </MarlinThemeProvider>
  );
}

export default App;
